import { lazy, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { SkipLink } from "@/components/Accessibility";

// Lazy load non-critical UI components to reduce main-thread work
const Toaster = lazy(() => import("@/components/ui/toaster").then(m => ({ default: m.Toaster })));
const Sonner = lazy(() => import("@/components/ui/sonner").then(m => ({ default: m.Toaster })));
const TooltipProvider = lazy(() => import("@/components/ui/tooltip").then(m => ({ default: m.TooltipProvider })));

// Lazy load pages for code splitting
const Home = lazy(() => import("./pages/Home"));
const Buteurs = lazy(() => import("./pages/Buteurs"));
const Paris = lazy(() => import("./pages/Paris"));
const MatchDetail = lazy(() => import("./pages/MatchDetail"));
const Contact = lazy(() => import("./pages/Contact"));
const About = lazy(() => import("./pages/About"));
const Support = lazy(() => import("./pages/Support"));
const Learning = lazy(() => import("./pages/Learning"));
const Auth = lazy(() => import("./pages/Auth"));
const Admin = lazy(() => import("./pages/Admin"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
    },
  },
});

// Minimal loading fallback
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider attribute="class" defaultTheme="light" enableSystem>
      <Suspense fallback={null}>
        <TooltipProvider>
          <Suspense fallback={null}>
            <Toaster />
            <Sonner />
          </Suspense>
          <BrowserRouter>
            <SkipLink />
            <Suspense fallback={<PageLoader />}>
              <main id="main-content">
              <Routes>
                {/* Main routes */}
                <Route path="/" element={<Home />} />
                <Route path="/buteurs" element={<Buteurs />} />
                <Route path="/paris" element={<Paris />} />
                <Route path="/match/:fixtureId" element={<MatchDetail />} />
                
                {/* Redirects from old routes */}
                <Route path="/fixtures" element={<Navigate to="/buteurs" replace />} />
                <Route path="/pronostics" element={<Navigate to="/buteurs" replace />} />
                <Route path="/xgboost" element={<Navigate to="/paris" replace />} />
                <Route path="/report" element={<Navigate to="/buteurs" replace />} />
                <Route path="/comparison" element={<Navigate to="/buteurs" replace />} />
                <Route path="/stats" element={<Navigate to="/buteurs" replace />} />
                
                {/* Info pages */}
                <Route path="/contact" element={<Contact />} />
                <Route path="/about" element={<About />} />
                <Route path="/support" element={<Support />} />
                
                {/* Auth & Admin */}
                <Route path="/auth" element={<Auth />} />
                <Route path="/admin" element={<Admin />} />
                <Route path="/learning" element={<Learning />} />
                
                {/* 404 */}
                <Route path="*" element={<NotFound />} />
              </Routes>
              </main>
            </Suspense>
          </BrowserRouter>
        </TooltipProvider>
      </Suspense>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
